import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
// @ts-expect-error
import Graphic from '@arcgis/core/Graphic';
// @ts-expect-error
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import {
  loadProjectOperator,
  projectGeometry,
  projectGeometryAsync,
} from 'esri/projectGeometry';
import {
  ImageViewerInstance,
  SyncGroup,
  SyncMode,
  LayerFeature,
  LayerFeatureGeometry,
  PanoramaState,
  SYNC_GROUP_COLORS,
  IMAGE_VIEWER_CASCADE_OFFSET_PX,
  IMAGE_VIEWER_DEFAULT_POSITION,
} from './types';
import {
  computeFovConeLengthMapUnits,
  hasMapResolutionDrifted,
  readMapViewResolution,
} from './fovConeGeometry';
import {
  getFeatureSyncValue,
  resolveSyncFocusModeChange,
  resolveSyncedJumpSource,
  syncFocusUnavailableMessage,
  SYNC_PARTIAL_NO_MATCH_MESSAGE,
  SYNC_SEQUENCE_END_MESSAGE,
  SYNC_SEQUENCE_START_MESSAGE,
} from 'utils/syncIndex';
import { getNextWindowZIndex } from 'utils/windowZIndex';
import {
  IMAGE_VIEWER_CAPACITY_LIMIT_EVENT,
  SIMULTANEOUS_IMAGES_LIMIT_MESSAGE,
  isAtImageViewerCapacity,
} from './imageViewerLimits';
import './style.scss';


/* ------------------------------------------------------------------ */
/*  Context shape                                                      */
/* ------------------------------------------------------------------ */

interface ImageViewerContextValue {
  viewers: ImageViewerInstance[];
  syncGroups: SyncGroup[];
  activeViewerId: string | null;
  capacityMessage: string | null;

  openViewer: (params: OpenViewerParams) => void;
  clearCapacityMessage: () => void;
  closeViewer: (id: string) => void;
  setActiveViewer: (
    id: string,
    options?: { autoPan?: boolean; raiseToFront?: boolean },
  ) => void;
  navigateViewer: (
    id: string,
    direction: 'next' | 'previous' | 'first' | 'last',
  ) => void;
  toggleSync: (viewerId: string) => void;
  setSyncFocusMode: (viewerId: string, useSpatialFocus: boolean) => void;
  jumpViewerToFeature: (viewerId: string, featureIndex: number) => void;
  updatePanoramaState: (id: string, state: Partial<PanoramaState>) => void;
  setViewerLoading: (id: string, loading: boolean) => void;
  setViewerError: (id: string, hasError: boolean) => void;
  setViewerMinimized: (id: string, minimized: boolean) => void;
  setViewerPosition: (id: string, pos: { x: number; y: number }) => void;
  getSyncGroupColor: (groupId: string | null) => string | undefined;
  getNextZIndex: () => number;
}

export interface OpenViewerParams {
  surveySlug: string;
  surveyLabel: string;
  layerKey: string;
  layerName: string;
  layerRef: unknown;
  filePath: string;
  imageType: 'STATIC' | 'PANO';
  geometry: LayerFeatureGeometry | null;
  featureIndex: number;
  layerFeatures: LayerFeature[];
  autoPan?: boolean;
}

const ImageViewerContext = createContext<ImageViewerContextValue | null>(null);

export function useImageViewers() {
  const ctx = useContext(ImageViewerContext);
  if (!ctx) throw new Error('useImageViewers must be used within ImageViewerProvider');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

interface ProviderProps {
  mapView: any;          // ArcGIS MapView
  children: React.ReactNode;
}

let viewerIdCounter = 0;
const nextViewerId = () => `img-viewer-${++viewerIdCounter}`;

let groupIdCounter = 0;
const nextGroupId = () => `sync-group-${++groupIdCounter}`;

export const ImageViewerProvider: React.FC<ProviderProps> = ({ mapView, children }) => {
  const [viewers, setViewers] = useState<ImageViewerInstance[]>([]);
  const [syncGroups, setSyncGroups] = useState<SyncGroup[]>([]);
  const [activeViewerId, setActiveViewerIdRaw] = useState<string | null>(null);
  const [capacityMessage, setCapacityMessage] = useState<string | null>(null);

  // Stable refs for callbacks that need current state without re-creating closures
  const viewersRef = useRef(viewers);
  viewersRef.current = viewers;
  const syncGroupsRef = useRef(syncGroups);
  syncGroupsRef.current = syncGroups;
  const highlightsRef = useRef<Record<string, any>>({});
  const fovConeGraphicRef = useRef<any>(null);
  const fovMapGeometryByViewerRef = useRef<Record<string, unknown>>({});
  // Retained cone size so navigate/pan redraws do not resize the wedge mid-gesture.
  const fovConeAppearanceByViewerRef = useRef<Record<string, {
    length: number;
    halfAngle: number;
    mapResolution: number | null;
  }>>({});
  const highlightRequestVersionRef = useRef<Record<string, number>>({});
  const syncRecenterRequestVersionRef = useRef(0);

  /* ---- Helpers ---- */

  const removeFovCone = useCallback(() => {
    const coneGraphic = fovConeGraphicRef.current;
    if (!coneGraphic || !mapView?.graphics) return;
    mapView.graphics.remove(coneGraphic);
    fovConeGraphicRef.current = null;
  }, [mapView]);

  const getGeometryPoint = useCallback((geometry: unknown) => {
    if (!geometry || typeof geometry !== 'object') return null;
    const geo = geometry as LayerFeatureGeometry;
    const type = typeof geo.type === 'string' ? geo.type.toLowerCase() : '';
    if (type === 'point') return geo;
    return geo.extent?.center ?? null;
  }, []);

  const toMapViewPoint = useCallback((geometry: unknown) => {
    const point = getGeometryPoint(geometry);
    if (!point || !mapView?.spatialReference) return point;
    if (typeof point.x !== 'number' || typeof point.y !== 'number') return point;

    const viewWkid = mapView.spatialReference?.wkid ?? mapView.spatialReference?.latestWkid;
    if (!viewWkid) return point;

    const existingWkid = point.spatialReference?.wkid ?? point.spatialReference?.latestWkid;
    const looksGeographic = Math.abs(point.x) <= 180 && Math.abs(point.y) <= 90;
    const pointWkid = existingWkid ?? (looksGeographic ? 4326 : viewWkid);
    if (pointWkid === viewWkid) {
      return point;
    }

    try {
      const source = existingWkid
        ? point
        : {
            type: 'point',
            x: point.x,
            y: point.y,
            spatialReference: { wkid: pointWkid },
          };
      return projectGeometry(source, mapView.spatialReference) || point;
    } catch {
      return point;
    }
  }, [getGeometryPoint, mapView]);

  useEffect(() => {
    if (!mapView) return;
    loadProjectOperator().catch(() => { });
  }, [mapView]);

  const readNumericBearing = useCallback((value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    // Keep bearing normalized to [0, 360).
    return ((parsed % 360) + 360) % 360;
  }, []);

  const getFeatureBearing = useCallback((feature: LayerFeature | null | undefined): number | null => {
    if (!feature) return null;
    const attrs = feature.attributes || {};
    const keys = [
      'course_bearing',
      'courseBearing',
      'course',
      'bearing',
      'heading',
      'camera_bearing',
      'camera_heading',
    ];

    for (const key of keys) {
      const direct = readNumericBearing(attrs[key]);
      if (direct !== null) return direct;

      const upper = readNumericBearing(attrs[key.toUpperCase()]);
      if (upper !== null) return upper;
    }

    return null;
  }, [readNumericBearing]);

  const getPanoramaYawDegrees = useCallback((state: PanoramaState | null | undefined): number => {
    if (!state || !Number.isFinite(state.yaw)) return 0;
    // Photo Sphere Viewer reports yaw in radians.
    return (state.yaw * 180) / Math.PI;
  }, []);

  const getFovHalfAngleDegrees = useCallback((state: PanoramaState | null | undefined): number => {
    const zoom = Number.isFinite(state?.zoom) ? Number(state?.zoom) : 50;
    const clampedZoom = Math.max(0, Math.min(100, zoom));
    const minFov = 30;
    const maxFov = 90;
    const fov = maxFov - (clampedZoom / 100) * (maxFov - minFov);
    return fov / 2;
  }, []);

  const updateFovCone = useCallback((
    viewerId: string,
    feature: LayerFeature,
    panoramaState?: PanoramaState | null,
    options?: {
      readonly retainAppearance?: boolean;
      readonly retainLength?: boolean;
      readonly retainHalfAngle?: boolean;
    },
  ) => {
    removeFovCone();

    if (!mapView?.graphics) return;
    if (feature.imageType !== 'PANO') return;

    const mapGeometry = fovMapGeometryByViewerRef.current[viewerId] ?? feature.geometry;
    const point = toMapViewPoint(mapGeometry);
    if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') return;
    const pointX = point.x;
    const pointY = point.y;

    const baseBearing = getFeatureBearing(feature);
    if (baseBearing === null) return;
    const yawDegrees = getPanoramaYawDegrees(panoramaState);
    const bearing = readNumericBearing(baseBearing + yawDegrees);
    if (bearing === null) return;

    const cached = fovConeAppearanceByViewerRef.current[viewerId];
    const mapResolution = readMapViewResolution(mapView);
    const resolutionDrifted = hasMapResolutionDrifted(
      cached?.mapResolution,
      mapResolution,
    );
    const retainLength =
      cached != null &&
      !resolutionDrifted &&
      (options?.retainAppearance === true || options?.retainLength === true);
    const retainHalfAngle =
      cached != null &&
      (options?.retainAppearance === true || options?.retainHalfAngle === true);

    const lengthFromView = computeFovConeLengthMapUnits({
      resolution: mapResolution,
      extentWidth:
        typeof mapView.extent?.width === 'number' ? mapView.extent.width : null,
      viewWidthPx: typeof mapView.width === 'number' ? mapView.width : null,
    });
    const coneLength = retainLength ? cached.length : lengthFromView;

    const halfAngleFromZoom = getFovHalfAngleDegrees(panoramaState);
    const halfAngle = retainHalfAngle ? cached.halfAngle : halfAngleFromZoom;

    fovConeAppearanceByViewerRef.current[viewerId] = {
      length: coneLength,
      halfAngle,
      mapResolution,
    };

    const projectBearing = (bearingDeg: number) => {
      const radians = (bearingDeg * Math.PI) / 180;
      return {
        x: pointX + coneLength * Math.sin(radians),
        y: pointY + coneLength * Math.cos(radians),
      };
    };

    const left = projectBearing(bearing - halfAngle);
    const right = projectBearing(bearing + halfAngle);

    const coneGraphic = new Graphic({
      geometry: {
        type: 'polygon',
        rings: [
          [
            [pointX, pointY],
            [left.x, left.y],
            [right.x, right.y],
            [pointX, pointY],
          ],
        ],
        spatialReference: point.spatialReference || mapView.spatialReference,
      },
      symbol: {
        type: 'simple-fill',
        color: [255, 153, 0, 0.2],
        outline: {
          color: [255, 153, 0, 0.9],
          width: 1.5,
        },
      },
      attributes: {
        viewerId,
        isFovCone: true,
      },
    } as any);

    mapView.graphics.add(coneGraphic);
    fovConeGraphicRef.current = coneGraphic;
  }, [
    getFeatureBearing,
    getFovHalfAngleDegrees,
    getPanoramaYawDegrees,
    mapView,
    readNumericBearing,
    removeFovCone,
    toMapViewPoint,
  ]);

  const autoPanToFeature = useCallback(
    (geometry: unknown) => {
      if (!mapView || !geometry || typeof geometry !== 'object') return;
      const extent = mapView.extent;
      if (!extent) return;

      const geo = geometry as LayerFeatureGeometry;
      const point = geo.type === 'point' ? geo : geo.extent?.center;
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') return;

      // Check if point is within 80 % of the extent (i.e. not near the edge)
      const padX = (extent.xmax - extent.xmin) * 0.1;
      const padY = (extent.ymax - extent.ymin) * 0.1;
      const insidePadded =
        point.x > extent.xmin + padX &&
        point.x < extent.xmax - padX &&
        point.y > extent.ymin + padY &&
        point.y < extent.ymax - padY;

      if (!insidePadded) {
        mapView.goTo({ target: point }, { duration: 500, easing: 'ease-in-out' }).catch(() => { });
      }
    },
    [mapView],
  );

  const highlightFeatureOnMap = useCallback(
    (
      viewerId: string,
      feature: LayerFeature,
      layerRef: any,
      options?: { autoPan?: boolean },
    ) => {
      if (!mapView || !layerRef || !feature?.filePath) return;
      const requestVersion = (highlightRequestVersionRef.current[viewerId] ?? 0) + 1;
      highlightRequestVersionRef.current[viewerId] = requestVersion;

      // Remove old highlight for this viewer
      if (highlightsRef.current[viewerId]) {
        highlightsRef.current[viewerId].remove();
        delete highlightsRef.current[viewerId];
      }
      removeFovCone();
      mapView.whenLayerView(layerRef).then((layerView: any) => {
        if (highlightRequestVersionRef.current[viewerId] !== requestVersion) return;

        // Find the graphic matching this feature's geometry
        layerRef.queryFeatures({
          where: `file_path = '${(feature.filePath || '').replace(/'/g, "''")}'`,
          returnGeometry: true,
          outFields: ['*'],
        }).then((result: any) => {
          if (highlightRequestVersionRef.current[viewerId] !== requestVersion) return;
          if (result.features?.length) {
            if (highlightsRef.current[viewerId]) {
              highlightsRef.current[viewerId].remove();
              delete highlightsRef.current[viewerId];
            }
            const hasWfsPointHighlight = Boolean(
              mapView?.highlights?.find?.((h: any) => h?.name === 'wfs-point-highlight'),
            );
            const nextHighlight = hasWfsPointHighlight
              ? layerView.highlight(result.features, { name: 'wfs-point-highlight' })
              : layerView.highlight(result.features);
            if (highlightRequestVersionRef.current[viewerId] !== requestVersion) {
              nextHighlight.remove();
              return;
            }
            highlightsRef.current[viewerId] = nextHighlight;
            const mapFeature = result.features[0] as {
              geometry?: LayerFeatureGeometry | null;
              attributes?: Record<string, unknown>;
            } | undefined;
            if (mapFeature?.geometry) {
              fovMapGeometryByViewerRef.current[viewerId] = mapFeature.geometry;
            }
            const featureForFov: LayerFeature = mapFeature?.attributes
              ? {
                  ...feature,
                  geometry: mapFeature.geometry ?? feature.geometry,
                  attributes: {
                    ...feature.attributes,
                    ...mapFeature.attributes,
                  },
                }
              : feature;
            const viewerPanoramaState = viewersRef.current.find((v) => v.id === viewerId)?.panoramaState;
            updateFovCone(viewerId, featureForFov, viewerPanoramaState, { retainAppearance: true });
            window.dispatchEvent(new CustomEvent('wfs-point-highlight:change', {
              detail: { count: Object.keys(highlightsRef.current).length },
            }));
            if (options?.autoPan !== false && mapFeature?.geometry) {
              autoPanToFeature(mapFeature.geometry);
            }
          }
        }).catch(() => { });
      }).catch(() => { });
    },
    [autoPanToFeature, mapView, removeFovCone, updateFovCone],
  );

  const goToSyncGroupCenter = useCallback(
    (
      group: SyncGroup,
      targetIndex: number,
      requestVersion?: number,
      syncLookup?: { readonly mode: SyncMode; readonly referenceValue: number },
    ) => {
      if (!mapView || group.viewerIds.length < 2) return;

      const rawPoints = group.viewerIds
        .map((viewerId) => {
          const viewer = viewersRef.current.find((v) => v.id === viewerId);
          if (!viewer) return null;

          let feature: LayerFeature | undefined = viewer.layerFeatures[targetIndex];
          if (syncLookup) {
            const matchedIndex = viewer.layerFeatures.findIndex(
              (candidate) =>
                getFeatureSyncValue(candidate, syncLookup.mode) ===
                syncLookup.referenceValue,
            );
            feature =
              matchedIndex >= 0 ? viewer.layerFeatures[matchedIndex] : undefined;
          }

          return getGeometryPoint(feature?.geometry);
        })
        .filter(Boolean);

      if (rawPoints.length === 0) return;

      const normalizeAndGoToCenter = async () => {
        const normalizedPoints = await Promise.all(
          rawPoints.map(async (point: { spatialReference?: { wkid?: number; latestWkid?: number }; x?: number; y?: number } | null) => {
            if (!point?.spatialReference || !mapView.spatialReference) return point;

            const sameWkid =
              point.spatialReference.wkid === mapView.spatialReference.wkid ||
              point.spatialReference.latestWkid === mapView.spatialReference.wkid;
            if (sameWkid) return point;

            try {
              return await projectGeometryAsync(point, mapView.spatialReference);
            } catch {
              return null;
            }
          }),
        );

        const points = normalizedPoints.filter(
          (p): p is { x: number; y: number } =>
            Boolean(p && typeof p.x === 'number' && typeof p.y === 'number'),
        );

        if (points.length === 0) return;

        if (
          typeof requestVersion === 'number' &&
          syncRecenterRequestVersionRef.current !== requestVersion
        ) return;

        if (points.length === 1) {
          mapView
            .goTo({ target: points[0] }, { duration: 500, easing: 'ease-in-out' })
            .catch(() => { });
          return;
        }

        const sum = points.reduce(
          (acc, point) => ({
            x: acc.x + point.x,
            y: acc.y + point.y,
          }),
          { x: 0, y: 0 },
        );

        const centerPoint = {
          type: 'point',
          x: sum.x / points.length,
          y: sum.y / points.length,
          spatialReference: mapView.spatialReference,
        };

        mapView.goTo({ target: centerPoint }, { duration: 500, easing: 'ease-in-out' }).catch(() => { });
      };

      void normalizeAndGoToCenter();
    },
    [getGeometryPoint, mapView],
  );

  const goToViewerFeaturePoint = useCallback(
    async (viewerId: string, targetIndex: number, requestVersion?: number) => {
      if (!mapView) return;

      const sourceViewer = viewersRef.current.find((v) => v.id === viewerId);
      if (!sourceViewer) return;

      const sourceFeature = sourceViewer.layerFeatures[targetIndex];
      const sourcePoint = getGeometryPoint(sourceFeature?.geometry);
      if (!sourcePoint) return;

      let targetPoint: any = sourcePoint;
      const sameWkid =
        sourcePoint?.spatialReference?.wkid === mapView.spatialReference?.wkid ||
        sourcePoint?.spatialReference?.latestWkid === mapView.spatialReference?.wkid;

      if (!sameWkid && sourcePoint?.spatialReference && mapView.spatialReference) {
        try {
          targetPoint = await projectGeometryAsync(
            sourcePoint,
            mapView.spatialReference,
          );
        } catch {
          targetPoint = null;
        }
      }

      if (!targetPoint) return;

      if (
        typeof requestVersion === 'number' &&
        syncRecenterRequestVersionRef.current !== requestVersion
      ) return;

      mapView.goTo({ target: targetPoint }, { duration: 500, easing: 'ease-in-out' }).catch(() => { });
    },
    [getGeometryPoint, mapView],
  );

  const removeHighlight = useCallback((viewerId: string) => {
    highlightRequestVersionRef.current[viewerId] =
      (highlightRequestVersionRef.current[viewerId] ?? 0) + 1;
    if (highlightsRef.current[viewerId]) {
      highlightsRef.current[viewerId].remove();
      delete highlightsRef.current[viewerId];
      window.dispatchEvent(new CustomEvent('wfs-point-highlight:change', {
        detail: { count: Object.keys(highlightsRef.current).length },
      }));
    }
    delete fovMapGeometryByViewerRef.current[viewerId];
    delete fovConeAppearanceByViewerRef.current[viewerId];
    removeFovCone();
  }, [removeFovCone]);

  const removeAllHighlightsExcept = useCallback((keepViewerId: string | null) => {
    Object.keys(highlightsRef.current).forEach((vid) => {
      if (vid !== keepViewerId) {
        highlightRequestVersionRef.current[vid] =
          (highlightRequestVersionRef.current[vid] ?? 0) + 1;
        highlightsRef.current[vid].remove();
        delete highlightsRef.current[vid];
        delete fovMapGeometryByViewerRef.current[vid];
        delete fovConeAppearanceByViewerRef.current[vid];
        removeFovCone();
      }
    });
    window.dispatchEvent(new CustomEvent('wfs-point-highlight:change', {
      detail: { count: Object.keys(highlightsRef.current).length },
    }));
  }, [removeFovCone]);

  /* ---- Actions ---- */

  const setActiveViewer = useCallback((
    id: string,
    options?: { autoPan?: boolean; raiseToFront?: boolean },
  ) => {
    const viewer = viewersRef.current.find((v) => v.id === id);
    const alreadyActive = viewer?.isActive === true;

    // Skip full activate re-renders when already active. Header drag starts with a
    // mousedown activate; re-rendering mid-drag desyncs controlled react-draggable.
    if (!alreadyActive) {
      setActiveViewerIdRaw(id);
      const newZ = getNextWindowZIndex();
      setViewers((prev) =>
        prev.map((v) => ({
          ...v,
          isActive: v.id === id,
          ...(v.id === id ? { zIndex: newZ } : {}),
        })),
      );
    } else if (options?.raiseToFront === true) {
      const newZ = getNextWindowZIndex();
      setViewers((prev) =>
        prev.map((v) => (v.id === id ? { ...v, zIndex: newZ } : v)),
      );
    }

    // Only the active viewer's data point should be highlighted on the map
    removeAllHighlightsExcept(id);
    if (viewer && !viewer.noImageAvailable && !highlightsRef.current[id]) {
      highlightFeatureOnMap(
        id,
        viewer.layerFeatures[viewer.sequentialIndex],
        viewer.layerRef,
        alreadyActive ? { ...options, autoPan: false } : options,
      );
    } else if (
      !alreadyActive &&
      viewer &&
      !viewer.noImageAvailable &&
      options?.autoPan
    ) {
      const feature = viewer.layerFeatures[viewer.sequentialIndex];
      const point = feature?.geometry?.type === 'point'
        ? feature.geometry
        : feature?.geometry?.extent?.center;
      if (point) {
        mapView?.goTo({ target: point }, { duration: 500, easing: 'ease-in-out' }).catch(() => { });
      }
    }
  }, [removeAllHighlightsExcept, highlightFeatureOnMap, mapView]);

  const clearCapacityMessage = useCallback(() => {
    setCapacityMessage(null);
  }, []);

  const showCapacityLimitMessage = useCallback(
    (message: string = SIMULTANEOUS_IMAGES_LIMIT_MESSAGE) => {
      setCapacityMessage(message);
    },
    [],
  );

  const openViewer = useCallback(
    (params: OpenViewerParams) => {
      const autoPan = params.autoPan !== false;

      // If a viewer for this layerKey already exists, jump it to the new feature
      const existing = viewersRef.current.find((v) => v.layerKey === params.layerKey);
      if (existing) {
        // jumpViewerToFeatureInternal owns the highlight here, so activate directly
        // without the re-highlight side-effect in setActiveViewer (which would race
        // against jumpViewerToFeatureInternal's async query using stale state)
        jumpViewerToFeatureInternal(
          existing.id,
          params.featureIndex,
          params.layerFeatures,
          { autoPan },
        );

        // If this viewer participates in sync, map clicks should keep all linked
        // viewers aligned on the same sequential point.
        if (existing.isSynced && existing.syncGroupId) {
          const group = syncGroupsRef.current.find((g) => g.id === existing.syncGroupId);
          if (group) {
            jumpSyncedViewers(group, params.featureIndex, existing.id, {
              recenterMap: autoPan,
            });
          }
        }

        setActiveViewerIdRaw(existing.id);
        const bumpZ = getNextWindowZIndex();
        setViewers((prev) => prev.map((v) => ({ ...v, isActive: v.id === existing.id, ...(v.id === existing.id ? { zIndex: bumpZ } : {}) })));
        removeAllHighlightsExcept(existing.id);
        return;
      }

      if (isAtImageViewerCapacity(viewersRef.current.length)) {
        showCapacityLimitMessage();
        return;
      }

      clearCapacityMessage();

      const id = nextViewerId();
      // Cascade position so multiple modals don't stack exactly.
      const offset = viewersRef.current.length * IMAGE_VIEWER_CASCADE_OFFSET_PX;
      const viewer: ImageViewerInstance = {
        id,
        surveySlug: params.surveySlug,
        surveyLabel: params.surveyLabel,
        layerKey: params.layerKey,
        layerName: params.layerName,
        layerRef: params.layerRef,
        currentFilePath: params.filePath,
        imageType: params.imageType,
        isActive: true,
        isSynced: false,
        syncGroupId: null,
        isMinimized: false,
        position: {
          x: IMAGE_VIEWER_DEFAULT_POSITION.x + offset,
          y: IMAGE_VIEWER_DEFAULT_POSITION.y + offset,
        },
        panoramaState: null,
        sequentialIndex: params.featureIndex,
        layerFeatures: params.layerFeatures,
        isLoading: true,
        hasError: false,
        noImageAvailable: false,
        zIndex: getNextWindowZIndex(),
      };

      setViewers((prev) => [...prev.map((v) => ({ ...v, isActive: false })), viewer]);
      setActiveViewerIdRaw(id);
      removeAllHighlightsExcept(null);
      highlightFeatureOnMap(
        id,
        params.layerFeatures[params.featureIndex],
        params.layerRef,
        { autoPan },
      );
    },
    [
      clearCapacityMessage,
      highlightFeatureOnMap,
      removeAllHighlightsExcept,
      showCapacityLimitMessage,
    ],
  );

  const closeViewer = useCallback(
    (id: string) => {
      removeHighlight(id);

      const viewer = viewersRef.current.find((v) => v.id === id);

      setViewers((prev) => {
        const remaining = prev.filter((v) => v.id !== id);
        // If the closed viewer was active, activate the last opened one
        if (viewer?.isActive && remaining.length > 0) {
          const last = remaining[remaining.length - 1];
          return remaining.map((v) => ({ ...v, isActive: v.id === last.id }));
        }
        return remaining;
      });

      if (viewer?.isActive) {
        const remaining = viewersRef.current.filter((v) => v.id !== id);
        const newActiveId = remaining.length > 0 ? remaining[remaining.length - 1].id : null;
        setActiveViewerIdRaw(newActiveId);

        // Re-highlight the newly active viewer's data point
        if (newActiveId) {
          const newActive = remaining.find((v) => v.id === newActiveId);
          if (newActive) {
            // Closing a viewer should not force the map to pan to another viewer.
            highlightFeatureOnMap(
              newActiveId,
              newActive.layerFeatures[newActive.sequentialIndex],
              newActive.layerRef,
              { autoPan: false },
            );
          }
        }
      }

      // Handle sync group cleanup
      if (viewer?.syncGroupId) {
        setSyncGroups((prev) => {
          return prev
            .map((g) => {
              if (g.id !== viewer.syncGroupId) return g;
              const newIds = g.viewerIds.filter((vid) => vid !== id);
              return {
                ...g,
                viewerIds: newIds,
                focusViewerId: g.focusViewerId === id ? null : g.focusViewerId,
              };
            })
            .filter((g) => {
              // If only one viewer remains, auto-unsync it
              if (g.viewerIds.length <= 1) {
                if (g.viewerIds.length === 1) {
                  const lastId = g.viewerIds[0];
                  setViewers((vPrev) =>
                    vPrev.map((v) =>
                      v.id === lastId ? { ...v, isSynced: false, syncGroupId: null } : v,
                    ),
                  );
                }
                return false; // remove group
              }
              return true;
            });
        });
      }
    },
    [removeHighlight, highlightFeatureOnMap],
  );

  const applyFeatureToViewer = useCallback(
    (viewer: ImageViewerInstance, featureIndex: number, features?: LayerFeature[]): Partial<ImageViewerInstance> => {
      const feats = features || viewer.layerFeatures;
      const feat = feats[featureIndex];
      if (!feat) return {};

      const isSameFeature =
        viewer.sequentialIndex === featureIndex &&
        viewer.currentFilePath === feat.filePath &&
        viewer.imageType === feat.imageType;

      // If the viewer already points at the same feature/image, do not force a
      // loading cycle. This prevents linked viewers from getting stuck loading
      // when sync is enabled while all viewers are already aligned.
      if (isSameFeature) {
        return {
          currentFilePath: feat.filePath,
          imageType: feat.imageType,
          sequentialIndex: featureIndex,
          layerFeatures: feats,
          isLoading: false,
          hasError: false,
          noImageAvailable: false,
        };
      }

      return {
        currentFilePath: feat.filePath,
        imageType: feat.imageType,
        sequentialIndex: featureIndex,
        layerFeatures: feats,
        isLoading: true,
        hasError: false,
        noImageAvailable: false,
      };
    },
    [],
  );

  const jumpViewerToFeatureInternal = useCallback(
    (
      viewerId: string,
      featureIndex: number,
      features?: LayerFeature[],
      options?: { autoPan?: boolean },
    ) => {
      setViewers((prev) =>
        prev.map((v) => {
          if (v.id !== viewerId) return v;
          const updates = applyFeatureToViewer(v, featureIndex, features);
          const updatedViewer = { ...v, ...updates };
          highlightFeatureOnMap(
            viewerId,
            (features || v.layerFeatures)[featureIndex],
            v.layerRef,
            options,
          );
          return updatedViewer;
        }),
      );
    },
    [applyFeatureToViewer, highlightFeatureOnMap],
  );

  const jumpViewerToFeature = useCallback(
    (viewerId: string, featureIndex: number) => {
      const viewer = viewersRef.current.find((v) => v.id === viewerId);
      if (!viewer) return;
      jumpViewerToFeatureInternal(viewerId, featureIndex);

      // If synced, jump all in group to same index
      if (viewer.isSynced && viewer.syncGroupId) {
        const group = syncGroupsRef.current.find((g) => g.id === viewer.syncGroupId);
        if (group) {
          jumpSyncedViewers(group, featureIndex, null);
        }
      }
    },
    [jumpViewerToFeatureInternal],
  );

  const navigateViewer = useCallback(
    (id: string, direction: 'next' | 'previous' | 'first' | 'last') => {
      const viewer = viewersRef.current.find((v) => v.id === id);
      if (!viewer) return;

      const lastIndex = viewer.layerFeatures.length - 1;
      let newIndex: number;
      if (direction === 'first') {
        newIndex = 0;
      } else if (direction === 'last') {
        newIndex = lastIndex;
      } else if (direction === 'next') {
        newIndex = viewer.sequentialIndex + 1;
      } else {
        newIndex = viewer.sequentialIndex - 1;
      }

      if (
        newIndex < 0 ||
        newIndex > lastIndex ||
        newIndex === viewer.sequentialIndex
      ) {
        if (
          viewer.isSynced &&
          viewer.syncGroupId &&
          (direction === 'next' || direction === 'previous')
        ) {
          const boundMessage =
            direction === 'next'
              ? SYNC_SEQUENCE_END_MESSAGE
              : SYNC_SEQUENCE_START_MESSAGE;
          setSyncGroups((prev) =>
            prev.map((g) =>
              g.id === viewer.syncGroupId
                ? { ...g, limitMessage: boundMessage }
                : g,
            ),
          );
        }
        return;
      }

      // Retain panorama state for panoramic viewers on navigation
      const retainPanoState = viewer.imageType === 'PANO' ? viewer.panoramaState : null;

      // In synced mode we do one final recenter for the group to avoid
      // multiple competing pan animations.
      const isSyncedNavigation = Boolean(viewer.isSynced && viewer.syncGroupId);
      jumpViewerToFeatureInternal(id, newIndex, undefined, {
        autoPan: !isSyncedNavigation,
      });

      // If panoramic, restore the pan/zoom state after navigation
      if (retainPanoState) {
        setViewers((prev) =>
          prev.map((v) => (v.id === id ? { ...v, panoramaState: retainPanoState } : v)),
        );
      }

      // If synced, propagate to group
      if (viewer.isSynced && viewer.syncGroupId) {
        const group = syncGroupsRef.current.find((g) => g.id === viewer.syncGroupId);
        if (group) {
          jumpSyncedViewers(group, newIndex, id);
        }
      }
    },
    [jumpViewerToFeatureInternal],
  );

  /**
   * Jump all viewers in a sync group to the feature matching the source
   * viewer's sync value (image or spatial index), except the initiating viewer.
   */
  const jumpSyncedViewers = useCallback(
    (
      group: SyncGroup,
      targetIndex: number,
      excludeViewerId: string | null,
      options?: {
        mode?: SyncMode;
        referenceValue?: number | null;
        recenterMap?: boolean;
      },
    ) => {
      const current = viewersRef.current;
      const requestVersion = syncRecenterRequestVersionRef.current + 1;
      syncRecenterRequestVersionRef.current = requestVersion;
      const syncMode = options?.mode ?? group.syncMode ?? 'image';
      const recenterMap = options?.recenterMap !== false;

      const source = resolveSyncedJumpSource(
        current,
        group.viewerIds,
        targetIndex,
        syncMode,
        excludeViewerId,
        options?.referenceValue,
      );
      const sourceViewerId = source.sourceViewerId;
      const sourceFeatureIndex = source.sourceFeatureIndex;
      const targetSyncValue = source.targetSyncValue;

      let unmatchedCount = 0;

      // Update every viewer except the initiator (already on targetIndex).
      for (const vid of group.viewerIds) {
        if (vid === excludeViewerId) continue;
        const v = current.find((vv) => vv.id === vid);
        if (!v) continue;

        const matchedIndex = targetSyncValue !== null
          ? v.layerFeatures.findIndex(
            (feature) => getFeatureSyncValue(feature, syncMode) === targetSyncValue,
          )
          : -1;

        if (matchedIndex < 0) {
          unmatchedCount += 1;
          removeHighlight(vid);
          setViewers((prev) =>
            prev.map((vv) =>
              vv.id === vid
                ? {
                  ...vv,
                  currentFilePath: '',
                  isLoading: false,
                  hasError: true,
                  noImageAvailable: true,
                }
                : vv,
            ),
          );
          continue;
        }

        // Retain panorama state for panoramic viewers
        const retainPano = v.imageType === 'PANO' ? v.panoramaState : null;

        jumpViewerToFeatureInternal(vid, matchedIndex, undefined, { autoPan: false });

        if (retainPano) {
          setViewers((prev) =>
            prev.map((vv) => (vv.id === vid ? { ...vv, panoramaState: retainPano } : vv)),
          );
        }
      }

      const limitMessage =
        targetSyncValue === null
          ? syncFocusUnavailableMessage(syncMode)
          : unmatchedCount > 0
            ? SYNC_PARTIAL_NO_MATCH_MESSAGE
            : null;

      setSyncGroups((prev) =>
        prev.map((g) =>
          g.id === group.id ? { ...g, limitMessage } : g,
        ),
      );

      // Re-center map once per synced navigation. Prefer the source feature;
      // fall back to the group centroid when the source has no geometry.
      if (recenterMap && targetSyncValue !== null) {
        const sourceViewer = sourceViewerId
          ? current.find((vv) => vv.id === sourceViewerId)
          : undefined;
        const sourceFeature = sourceViewer?.layerFeatures[sourceFeatureIndex];
        const sourceHasGeometry = Boolean(getGeometryPoint(sourceFeature?.geometry));

        if (sourceHasGeometry && sourceViewerId) {
          void goToViewerFeaturePoint(
            sourceViewerId,
            sourceFeatureIndex,
            requestVersion,
          );
        } else {
          goToSyncGroupCenter(group, sourceFeatureIndex, requestVersion, {
            mode: syncMode,
            referenceValue: targetSyncValue,
          });
        }
      }
    },
    [
      getGeometryPoint,
      goToSyncGroupCenter,
      goToViewerFeaturePoint,
      jumpViewerToFeatureInternal,
      removeHighlight,
    ],
  );

  const setSyncFocusMode = useCallback(
    (viewerId: string, useSpatialFocus: boolean) => {
      const viewer = viewersRef.current.find((v) => v.id === viewerId);
      if (!viewer || !viewer.isSynced || !viewer.syncGroupId) return;

      const group = syncGroupsRef.current.find((g) => g.id === viewer.syncGroupId);
      if (!group) return;

      const sourceFeature = viewer.layerFeatures[viewer.sequentialIndex];
      const decision = resolveSyncFocusModeChange(
        sourceFeature,
        useSpatialFocus,
        viewerId,
      );

      if (!decision.ok) {
        setSyncGroups((prev) =>
          prev.map((g) =>
            g.id === group.id ? { ...g, limitMessage: decision.message } : g,
          ),
        );
        return;
      }

      setSyncGroups((prev) =>
        prev.map((g) =>
          g.id === group.id
            ? {
              ...g,
              syncMode: decision.mode,
              focusViewerId: decision.focusViewerId,
              limitMessage: null,
            }
            : g,
        ),
      );

      jumpSyncedViewers(
        {
          ...group,
          syncMode: decision.mode,
          focusViewerId: decision.focusViewerId,
        },
        viewer.sequentialIndex,
        viewerId,
        { mode: decision.mode, referenceValue: decision.referenceValue },
      );
    },
    [jumpSyncedViewers],
  );

  const toggleSync = useCallback(
    (viewerId: string) => {
      const viewer = viewersRef.current.find((v) => v.id === viewerId);
      if (!viewer) return;

      if (viewer.isSynced && viewer.syncGroupId) {
        // ---- UN-SYNC ----
        setSyncGroups((prev) =>
          prev
            .map((g) => {
              if (g.id !== viewer.syncGroupId) return g;
              const newIds = g.viewerIds.filter((vid) => vid !== viewerId);
              return {
                ...g,
                viewerIds: newIds,
                focusViewerId: g.focusViewerId === viewerId ? null : g.focusViewerId,
                limitMessage: null,
              };
            })
            .filter((g) => {
              if (g.viewerIds.length <= 1) {
                if (g.viewerIds.length === 1) {
                  const lastId = g.viewerIds[0];
                  setViewers((vPrev) =>
                    vPrev.map((v) =>
                      v.id === lastId ? { ...v, isSynced: false, syncGroupId: null } : v,
                    ),
                  );
                }
                return false;
              }
              return true;
            }),
        );
        setViewers((prev) =>
          prev.map((v) =>
            v.id === viewerId ? { ...v, isSynced: false, syncGroupId: null } : v,
          ),
        );
      } else {
        // ---- SYNC (or RE-SYNC) ----
        // Find existing group for this survey
        const existingGroup = syncGroupsRef.current.find(
          (g) => g.surveySlug === viewer.surveySlug,
        );

        if (existingGroup) {
          // Join existing group
          setSyncGroups((prev) =>
            prev.map((g) =>
              g.id === existingGroup.id
                ? { ...g, viewerIds: [...g.viewerIds, viewerId], limitMessage: null }
                : g,
            ),
          );
          setViewers((prev) =>
            prev.map((v) =>
              v.id === viewerId
                ? { ...v, isSynced: true, syncGroupId: existingGroup.id }
                : v,
            ),
          );

          // Re-sync pull: the re-joining viewer becomes source of truth
          // Jump all existing group members to this viewer's index
          const group = {
            ...existingGroup,
            viewerIds: [...existingGroup.viewerIds, viewerId],
          };
          const groupMode = existingGroup.syncMode ?? 'image';
          const joiningFeature = viewer.layerFeatures[viewer.sequentialIndex];
          const joiningReference = getFeatureSyncValue(joiningFeature, groupMode);
          if (joiningReference !== null) {
            jumpSyncedViewers(group, viewer.sequentialIndex, viewerId, {
              mode: groupMode,
              referenceValue: joiningReference,
            });
          } else {
            const fallbackSourceId = existingGroup.viewerIds[0] ?? viewerId;
            const fallbackViewer = viewersRef.current.find((v) => v.id === fallbackSourceId);
            const fallbackIndex = fallbackViewer?.sequentialIndex ?? viewer.sequentialIndex;
            jumpSyncedViewers(group, fallbackIndex, fallbackSourceId, {
              mode: groupMode,
            });
          }
        } else {
          // No group exists yet — check if there are other viewers for this survey
          const sameSurveyViewers = viewersRef.current.filter(
            (v) => v.surveySlug === viewer.surveySlug && v.id !== viewerId,
          );

          if (sameSurveyViewers.length === 0) {
            // Only one viewer for this survey, mark as "wants sync" but no group yet
            // The group will be created when another viewer enables sync
            const gid = nextGroupId();
            setSyncGroups((prev) => [
              ...prev,
              {
                id: gid,
                surveySlug: viewer.surveySlug,
                viewerIds: [viewerId],
                syncMode: 'image',
                focusViewerId: null,
                limitMessage: null,
              },
            ]);
            setViewers((prev) =>
              prev.map((v) =>
                v.id === viewerId ? { ...v, isSynced: true, syncGroupId: gid } : v,
              ),
            );
          } else {
            // Create a new group with this viewer
            const gid = nextGroupId();
            setSyncGroups((prev) => [
              ...prev,
              {
                id: gid,
                surveySlug: viewer.surveySlug,
                viewerIds: [viewerId],
                syncMode: 'image',
                focusViewerId: null,
                limitMessage: null,
              },
            ]);
            setViewers((prev) =>
              prev.map((v) =>
                v.id === viewerId ? { ...v, isSynced: true, syncGroupId: gid } : v,
              ),
            );
          }
        }
      }
    },
    [jumpSyncedViewers],
  );

  const updatePanoramaState = useCallback((id: string, partial: Partial<PanoramaState>) => {
    const viewer = viewersRef.current.find((v) => v.id === id);
    if (!viewer) return;

    const previous = viewer.panoramaState;
    // Merge outside setState so FOV can update immediately. 
    const mergedState: PanoramaState = {
      yaw: Number.isFinite(partial.yaw) ? Number(partial.yaw) : (previous?.yaw ?? 0),
      pitch: Number.isFinite(partial.pitch) ? Number(partial.pitch) : (previous?.pitch ?? 0),
      zoom: Number.isFinite(partial.zoom) ? Number(partial.zoom) : (previous?.zoom ?? 50),
    };

    // Keep ref in sync for any follow-up work in this tick before React commits.
    viewersRef.current = viewersRef.current.map((v) =>
      v.id === id ? { ...v, panoramaState: mergedState } : v,
    );

    setViewers((prev) =>
      prev.map((v) => (v.id === id ? { ...v, panoramaState: mergedState } : v)),
    );

    // Keep map FOV in sync with live panorama yaw/zoom while user looks around.
    // Retain length while panning at the same map zoom; length refreshes when map
    // resolution has drifted. Half-angle updates only when panorama zoom changes.
    if (!viewer.isActive || !highlightsRef.current[id]) return;
    const feature = viewer.layerFeatures[viewer.sequentialIndex];
    if (!feature || feature.imageType !== 'PANO') return;
    const zoomChanged = Number.isFinite(partial.zoom);
    updateFovCone(id, feature, mergedState, {
      retainLength: true,
      retainHalfAngle: !zoomChanged,
    });
  }, [updateFovCone]);

  // When the map finishes zooming/panning, resize the active FOV cone to match
  // the new resolution so it stays a readable on-screen size.
  useEffect(() => {
    if (!mapView) return;

    const refreshActiveConeForMapScale = () => {
      const activeId = viewersRef.current.find((v) => v.isActive)?.id;
      if (!activeId || !highlightsRef.current[activeId]) return;
      const viewer = viewersRef.current.find((v) => v.id === activeId);
      if (!viewer || viewer.noImageAvailable) return;
      const feature = viewer.layerFeatures[viewer.sequentialIndex];
      if (!feature || feature.imageType !== 'PANO') return;
      updateFovCone(activeId, feature, viewer.panoramaState, {
        retainHalfAngle: true,
      });
    };

    const handle = reactiveUtils.watch(
      () => mapView.stationary as boolean,
      (isStationary: boolean) => {
        if (isStationary) {
          refreshActiveConeForMapScale();
        }
      },
    );

    return () => {
      handle?.remove();
    };
  }, [mapView, updateFovCone]);

  const setViewerLoading = useCallback((id: string, loading: boolean) => {
    setViewers((prev) =>
      prev.map((v) => (v.id === id ? { ...v, isLoading: loading } : v)),
    );
  }, []);

  const setViewerError = useCallback((id: string, hasError: boolean) => {
    setViewers((prev) =>
      prev.map((v) => (
        v.id === id
          ? { ...v, hasError, isLoading: false, noImageAvailable: hasError ? v.noImageAvailable : false }
          : v
      )),
    );
  }, []);

  const setViewerMinimized = useCallback((id: string, minimized: boolean) => {
    setViewers((prev) =>
      prev.map((v) => (v.id === id ? { ...v, isMinimized: minimized } : v)),
    );
  }, []);

  const setViewerPosition = useCallback((id: string, pos: { x: number; y: number }) => {
    setViewers((prev) =>
      prev.map((v) => (v.id === id ? { ...v, position: pos } : v)),
    );
  }, []);

  const getSyncGroupColor = useCallback(
    (groupId: string | null): string | undefined => {
      if (!groupId) return undefined;
      const idx = syncGroupsRef.current.findIndex((g) => g.id === groupId);
      if (idx < 0) return undefined;
      return SYNC_GROUP_COLORS[idx % SYNC_GROUP_COLORS.length];
    },
    [],
  );

  const getNextZIndex = useCallback(() => getNextWindowZIndex(), []);

  /* ---- Listen for open requests from Dashboard click handler ---- */

  useEffect(() => {
    const handleOpenEvent = (ev: Event) => {
      const detail = (ev as CustomEvent)?.detail as OpenViewerParams | undefined;
      if (detail) openViewer(detail);
    };
    window.addEventListener('image-viewer:open', handleOpenEvent as EventListener);
    return () => window.removeEventListener('image-viewer:open', handleOpenEvent as EventListener);
  }, [openViewer]);

  useEffect(() => {
    const handleCapacityLimit = (ev: Event) => {
      const detail = (ev as CustomEvent<{ message?: string }>).detail;
      showCapacityLimitMessage(
        detail?.message ?? SIMULTANEOUS_IMAGES_LIMIT_MESSAGE,
      );
    };
    window.addEventListener(
      IMAGE_VIEWER_CAPACITY_LIMIT_EVENT,
      handleCapacityLimit as EventListener,
    );
    return () =>
      window.removeEventListener(
        IMAGE_VIEWER_CAPACITY_LIMIT_EVENT,
        handleCapacityLimit as EventListener,
      );
  }, [showCapacityLimitMessage]);

  /* ---- Close viewers when their layer is toggled off in the Survey Layer Panel ---- */

  useEffect(() => {
    const handleCloseLayer = (ev: Event) => {
      const { layerKey } = (ev as CustomEvent).detail as { layerKey: string };
      viewersRef.current
        .filter((v) => v.layerKey === layerKey)
        .forEach((v) => closeViewer(v.id));
    };
    window.addEventListener('image-viewer:close-layer', handleCloseLayer as EventListener);
    return () => window.removeEventListener('image-viewer:close-layer', handleCloseLayer as EventListener);
  }, [closeViewer]);

  /* ---- Close viewers when their survey is removed from the Survey Layer Panel ---- */

  useEffect(() => {
    const handleCloseSurvey = (ev: Event) => {
      const { surveySlug } = (ev as CustomEvent).detail as { surveySlug: string };
      viewersRef.current
        .filter((v) => v.surveySlug === surveySlug)
        .forEach((v) => closeViewer(v.id));
    };
    window.addEventListener('image-viewer:close-survey', handleCloseSurvey as EventListener);
    return () => window.removeEventListener('image-viewer:close-survey', handleCloseSurvey as EventListener);
  }, [closeViewer]);

  /* ---- Close all viewers when playback mode takes over ---- */

  useEffect(() => {
    const handleCloseAll = () => {
      viewersRef.current.forEach((v) => closeViewer(v.id));
    };
    window.addEventListener('image-viewer:close-all', handleCloseAll as EventListener);
    return () => window.removeEventListener('image-viewer:close-all', handleCloseAll as EventListener);
  }, [closeViewer]);

  useEffect(() => {
    return () => {
      if (fovConeGraphicRef.current && mapView?.graphics) {
        mapView.graphics.remove(fovConeGraphicRef.current);
      }
      fovConeGraphicRef.current = null;
    };
  }, [mapView]);

  /* ---- Keyboard navigation ---- */

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeViewerId) return;
      // Don't intercept if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateViewer(activeViewerId, 'next');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateViewer(activeViewerId, 'previous');
      } else if (e.key === 'Home') {
        e.preventDefault();
        navigateViewer(activeViewerId, 'first');
      } else if (e.key === 'End') {
        e.preventDefault();
        navigateViewer(activeViewerId, 'last');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeViewerId, navigateViewer]);

  /* ---- Context value ---- */

  const value: ImageViewerContextValue = {
    viewers,
    syncGroups,
    activeViewerId,
    capacityMessage,
    openViewer,
    clearCapacityMessage,
    closeViewer,
    setActiveViewer,
    navigateViewer,
    toggleSync,
    setSyncFocusMode,
    jumpViewerToFeature,
    updatePanoramaState,
    setViewerLoading,
    setViewerError,
    setViewerMinimized,
    setViewerPosition,
    getSyncGroupColor,
    getNextZIndex,
  };

  return (
    <ImageViewerContext.Provider value={value}>
      {children}
    </ImageViewerContext.Provider>
  );
};
